import React, { useState, useEffect, useRef } from "react";

function App() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [files, setFiles] = useState([]);
  const [theme, setTheme] = useState("light");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT;
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;

  const loginUrl = `https://${cognitoDomain}/login?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
  const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${redirectUri}`;

  // 1. Check for auth code and exchange for access_token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (code) {
      const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      fetch(tokenEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Token response:", data);
          if (data.access_token) {
            localStorage.setItem("access_token", data.access_token);
            setIsLoggedIn(true);
            window.history.replaceState({}, document.title, window.location.pathname);
            window.location.reload();
          }
        })
        .catch((err) => {
          console.error("Token fetch failed:", err);
        });
    } else {
      const token = localStorage.getItem("access_token");
      if (token) setIsLoggedIn(true);
    }
  }, []);

  // 2. Fetch files with auth header
  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_URL}files`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`Status: ${res.status}`);
      const data = await res.json();
      setFiles(data);
    } catch (error) {
      console.error("Error fetching files:", error);
      setStatus("❌ Unauthorized or fetch error.");
    }
  };

  useEffect(() => {
    if (isLoggedIn) fetchFiles();
  }, [isLoggedIn]);

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const styles = {
    container: {
      backgroundColor: theme === "light" ? "#fff" : "#1e1e1e",
      color: theme === "light" ? "#333" : "#f0f0f0",
      minHeight: "100vh",
      padding: "2rem",
      fontFamily: "'Segoe UI', sans-serif",
    },
    topBar: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "1rem",
    },
    toggle: {
      padding: "0.4rem 1rem",
      border: "none",
      borderRadius: "4px",
      backgroundColor: theme === "light" ? "#007bff" : "#3399ff",
      color: "#fff",
      cursor: "pointer",
    },
    authBtn: {
      padding: "0.4rem 1rem",
      border: "none",
      borderRadius: "4px",
      backgroundColor: "#6c63ff",
      color: "#fff",
      cursor: "pointer",
    },
    inputGroup: {
      display: "flex",
      alignItems: "center",
      marginBottom: "1rem",
    },
    input: {
      padding: "0.5rem",
      border: `1px solid ${theme === "light" ? "#ddd" : "#444"}`,
      borderRadius: "4px",
      fontSize: "1rem",
      backgroundColor: theme === "light" ? "#f9f9f9" : "#2c2c2c",
      color: theme === "light" ? "#333" : "#f0f0f0",
    },
    button: {
      marginLeft: "1rem",
      backgroundColor: theme === "light" ? "#007bff" : "#3399ff",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      padding: "0.6rem 1.2rem",
      cursor: "pointer",
    },
    card: {
      padding: "1rem",
      marginBottom: "1rem",
      backgroundColor: theme === "light" ? "#f9f9f9" : "#2c2c2c",
      border: `1px solid ${theme === "light" ? "#ddd" : "#444"}`,
      borderRadius: "6px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    actionButtons: {
      display: "flex",
      gap: "10px",
    },
    downloadBtn: {
      textDecoration: "none",
      backgroundColor: "#28a745",
      color: "#fff",
      padding: "6px 12px",
      borderRadius: "4px",
    },
    deleteBtn: {
      backgroundColor: "#dc3545",
      color: "#fff",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
    },
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus("");
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file.");
      return;
    }

    const token = localStorage.getItem("access_token");

    try {
      const response = await fetch(`${API_URL}upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ filename: file.name }),
      });

      const data = await response.json();
      if (!data.upload_url) {
        setStatus("Failed to get upload URL.");
        return;
      }

      const upload = await fetch(data.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (upload.ok) {
        setStatus("✅ File uploaded successfully!");
        fetchFiles();
      } else {
        setStatus("❌ Upload to S3 failed.");
      }
    } catch (err) {
      console.error(err);
      setStatus("❌ Something went wrong.");
    }
  };

  const handleDelete = async (file_id) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    const token = localStorage.getItem("access_token");

    try {
      const res = await fetch(`${API_URL}files/${file_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        setStatus("🗑️ File deleted.");
        fetchFiles();
      } else {
        setStatus("❌ Failed to delete.");
      }
    } catch (err) {
      console.error(err);
      setStatus("❌ Error deleting file.");
    }
  };

  const handleDownload = async (file_id) => {
    const token = localStorage.getItem("access_token");

    try {
      const res = await fetch(`${API_URL}download/${file_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (data.download_url) {
        window.open(data.download_url, "_blank");
      } else {
        alert("Failed to get download link.");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching download link.");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button onClick={toggleTheme} style={styles.toggle}>
          {theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}
        </button>
        {!isLoggedIn ? (
          <button onClick={() => (window.location.href = loginUrl)} style={styles.authBtn}>
            🔐 Login
          </button>
        ) : (
          <button onClick={() => {
            localStorage.removeItem("access_token");
            window.location.href = logoutUrl;
          }} style={styles.authBtn}>
            🚪 Logout
          </button>
        )}
      </div>

      <h2>📁 CAS Cloud Document Manager</h2>

      {isLoggedIn ? (
        <>
          <div style={styles.inputGroup}>
            <input
              ref={fileInputRef}
              type="file"
              style={styles.input}
              onChange={handleFileChange}
            />
            <button style={styles.button} onClick={handleUpload}>
              Upload
            </button>
          </div>

          {status && <p>{status}</p>}

          <h3>📄 Uploaded Files</h3>
          {files.length === 0 ? (
            <p>No files uploaded yet.</p>
          ) : (
            files.map((f) => (
              <div key={f.document_id} style={styles.card}>
                <span>{f.filename}</span>
                <div style={styles.actionButtons}>
                  <button onClick={() => handleDownload(f.document_id)} style={styles.downloadBtn}>
                    Download
                  </button>
                  <button onClick={() => handleDelete(f.document_id)} style={styles.deleteBtn}>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      ) : (
        <p>🔐 Please log in to view or upload documents.</p>
      )}
    </div>
  );
}

export default App;
